import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import CountBox from "@/components/ui/CountBox";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import Table, { TableColumn } from "@/components/ui/Table";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useReportList, useDeleteSaleSlip } from "@/hooks/performance/useSaleSlip";
import { ReportData } from "@/interface";
import SelectBox from "@/components/ui/SelectBox";
import PerformanceModal from "@/components/modal/performance/PerformanceModal";
import { toast } from "sonner";
import { useUserInfo } from "@/hooks/member/useMember";
import { getToday } from "@/lib/date";

export default function Report() {
  const [company, setCompany] = useState<string>("전체");
  const [reportType, setReportType] = useState<string>("1");
  const [startDate, setStartDate] = useState<string>(
    (() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1; // 0-based이므로 +1
      // YYYY-MM-01 형식으로 해당 월의 1일 반환
      return `${year}-${String(month).padStart(2, "0")}-01`;
    })()
  );
  const [endDate, setEndDate] = useState<string>(getToday());
  const [rangeBtn, setRangeBtn] = useState("today");
  const [topCompany, setTopCompany] = useState<string>("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableVer, setTableVer] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [allData, setAllData] = useState<ReportData[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedData, setSelectedData] = useState<ReportData | undefined>(undefined);

  const { mutate: deleteSaleSlip, isPending: isDeleting } = useDeleteSaleSlip();
  const { data: userInfo } = useUserInfo();

  const { data: realData } = useReportList(
    {
      page: currentPage,
      size: 20,
      dateFrom: startDate,
      dateTo: endDate,
      reportType: Number(reportType),
    },
    {
      enabled: !!startDate && !!endDate && !!reportType,
    }
  );
  console.log(realData);

  // 조회 조건 변경 시 페이지네이션 상태 초기화
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [startDate, endDate, reportType]);

  // 새 페이지 데이터 반영
  useEffect(() => {
    if (realData && realData.content) {
      if (currentPage === 0) {
        setAllData(realData.content);
        setTotalElements(realData.totalElements || 0);
      } else if (realData.content.length > 0) {
        setAllData(prev => [...prev, ...realData.content!]);
      }
      setLoadingMore(false);
    }
  }, [realData, currentPage]);

  const hasMore = useMemo(() => {
    if (!realData) return false;
    return !realData.last && allData.length < (realData.totalElements || 0);
  }, [realData?.last, allData.length, realData?.totalElements]);

  // 무한 스크롤 핸들러
  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) return;
      // 현재 표시된 데이터의 80% 이상 스크롤 시 다음 페이지 로드
      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  // 컬럼 정의 (reportType에 따라 동적 구성)
  const columns: TableColumn[] = useMemo(() => {
    if (reportType === "1") {
      // 계약 + 운송
      return [
        // 운송의뢰자
        {
          key: "custName",
          header: "업체명",
          group: "운송의뢰자",
          width: "200px",
          headerAlign: "center",
          bodyAlign: "left",
        },
        {
          key: "custBizNumber",
          header: "사업자등록번호",
          group: "운송의뢰자",
          width: "150px",
          headerAlign: "center",
          bodyAlign: "center",
        },
        // 계약정보
        {
          key: "contractUniqueNo",
          header: "계약고유번호",
          group: "계약정보",
          width: "150px",
          headerAlign: "center",
          bodyAlign: "center",
        },
        {
          key: "contractDate",
          header: "계약일자",
          group: "계약정보",
          width: "120px",
          headerAlign: "center",
          bodyAlign: "center",
          render: (value: string) => {
            if (!value) return "";
            return value.replace("T", " ").replace(/\.\d{3}Z?$/, "");
          },
        },
        {
          key: "contractAmount",
          header: "계약금액",
          group: "계약정보",
          width: "120px",
          headerAlign: "center",
          bodyAlign: "right",
          render: (value: number) => (value ? value.toLocaleString() : ""),
        },
        {
          key: "itemGroupCode",
          header: "이사화물/동일향만내이송",
          group: "계약정보",
          width: "180px",
          headerAlign: "center",
          bodyAlign: "center",
        },
        {
          key: "otherTransportUse",
          header: "타운송수단 이용여부",
          group: "계약정보",
          width: "150px",
          headerAlign: "center",
          bodyAlign: "center",
        },
        // 배차정보
        {
          key: "vehicleNumber",
          header: "차량등록번호",
          group: "배차정보",
          width: "130px",
          headerAlign: "center",
          bodyAlign: "center",
        },
        {
          key: "transportDoneDate",
          header: "운송완료일",
          group: "배차정보",
          width: "120px",
          headerAlign: "center",
          bodyAlign: "center",
          render: (value: string) => {
            if (!value) return "";
            return value.replace("T", " ").replace(/\.\d{3}Z?$/, "");
          },
        },
        {
          key: "transportFee",
          header: "운송료",
          group: "배차정보",
          width: "120px",
          headerAlign: "center",
          bodyAlign: "right",
          render: (value: number) => (value ? value.toLocaleString() : ""),
        },
      ];
    }

    // 계약 + 위탁
    return [
      // 운송의뢰자
      {
        key: "custName",
        header: "업체명",
        group: "운송의뢰자",
        width: "200px",
        headerAlign: "center",
        bodyAlign: "left",
      },
      {
        key: "custBizNumber",
        header: "사업자등록번호",
        group: "운송의뢰자",
        width: "150px",
        headerAlign: "center",
        bodyAlign: "center",
      },
      // 계약정보
      {
        key: "contractUniqueNo",
        header: "계약고유번호",
        group: "계약정보",
        width: "150px",
        headerAlign: "center",
        bodyAlign: "center",
      },
      {
        key: "contractDate",
        header: "계약일자",
        group: "계약정보",
        width: "120px",
        headerAlign: "center",
        bodyAlign: "center",
      },
      {
        key: "contractAmount",
        header: "계약금액",
        group: "계약정보",
        width: "120px",
        headerAlign: "center",
        bodyAlign: "right",
        render: (value: number) => (value ? value.toLocaleString() : ""),
      },
      {
        key: "itemGroupCode",
        header: "이사화물/동일향만내이송",
        group: "계약정보",
        width: "180px",
        headerAlign: "center",
        bodyAlign: "center",
      },
      {
        key: "otherTransportUse",
        header: "타운송수단 이용여부",
        group: "계약정보",
        width: "150px",
        headerAlign: "center",
        bodyAlign: "center",
      },
      // 위탁계약정보
      {
        key: "consignBizNumber",
        header: "사업자등록번호",
        group: "위탁계약정보",
        width: "150px",
        headerAlign: "center",
        bodyAlign: "center",
      },
      {
        key: "consignContractDate",
        header: "계약일자",
        group: "위탁계약정보",
        width: "120px",
        headerAlign: "center",
        bodyAlign: "center",
        render: (value: string) => {
          if (!value) return "";
          return value.replace("T", " ").replace(/\.\d{3}Z?$/, "");
        },
      },
      {
        key: "consignContractAmt",
        header: "위탁계약금액",
        group: "위탁계약정보",
        width: "120px",
        headerAlign: "center",
        bodyAlign: "right",
        render: (value: number) => (value ? value.toLocaleString() : ""),
      },
      {
        key: "freightNetUse",
        header: "화물정보망 이용여부",
        group: "위탁계약정보",
        width: "150px",
        headerAlign: "center",
        bodyAlign: "center",
      },
    ];
  }, [reportType]);

  // 날짜 버튼 선택 시 처리
  const handleDateSelect = (range: { startDate: string; endDate: string }) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const handleReset = () => {
    setCompany("전체");
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // 0-based이므로 +1
    // YYYY-MM-01 형식으로 해당 월의 1일 반환
    const firstDay = `${year}-${String(month).padStart(2, "0")}-01`;
    setStartDate(firstDay);
    setEndDate(getToday());
    setRangeBtn("today");
    setTopCompany("");
  };

  const handleSearch = () => {
    // TODO: 검색 로직
    console.log("검색:", { company, startDate, endDate, topCompany });
  };

  const handleAddClick = () => {
    setModalMode("add");
    setSelectedData(undefined);
    setIsModalOpen(true);
  };

  const handleEditClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("수정할 항목을 선택해주세요.");
      return;
    }

    if (selectedRows.length > 1) {
      toast.warning("수정할 항목은 1개만 선택해주세요.");
      return;
    }

    const selectedIndex = selectedRows[0];
    if (selectedIndex >= 0 && selectedIndex < allData.length) {
      setModalMode("edit");
      setSelectedData(allData[selectedIndex]);
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedData(undefined);
    setSelectedRows([]);
  };

  const handleModalSave = () => {
    // TODO: 저장 로직 구현
    toast.success(modalMode === "add" ? "추가되었습니다." : "수정되었습니다.");
    handleModalClose();
  };

  const handleDeleteClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 항목을 선택해주세요.");
      return;
    }

    // 선택된 항목들의 코드 추출
    const saleSlipCodes = selectedRows
      .map(index => {
        if (index >= 0 && index < allData.length) {
          return allData[index].saleSlipCodeForTieBreak;
        }
        return null;
      })
      .filter((code): code is string => code !== null && code !== undefined);

    if (saleSlipCodes.length === 0) {
      toast.warning("삭제할 수 있는 항목이 없습니다.");
      return;
    }

    const delId = userInfo?.name || "";
    if (!delId) {
      toast.error("사용자 정보를 가져올 수 없습니다.");
      return;
    }

    deleteSaleSlip(
      {
        saleSlipCodes,
        orderSlipCodes: [],
        delId,
      },
      {
        onSuccess: () => {
          setSelectedRows([]);
          // 데이터 새로고침
          setAllData([]);
          setCurrentPage(0);
          setTotalElements(0);
          setTableVer(v => v + 1);
        },
      }
    );
  };

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex items-end">
          <SelectBox
            label="보고서"
            value={reportType}
            onChange={setReportType}
            options={[
              { value: "1", label: "계약 + 운송" },
              { value: "2", label: "계약 + 위탁" },
            ]}
            className="w-[180px] mr-3"
          />
          <DateBox
            label="조회기간"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-3" />
          <DateSearchBtn
            mode="range"
            value={rangeBtn}
            onChangeValue={setRangeBtn}
            currentRange={{ startDate, endDate }}
            onDateSelect={handleDateSelect}
          />
          <div className="mr-3" />

          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* Main Content */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={totalElements} />
          <div className="flex gap-2">
            <PermissionButton permission="canUpdate" variant="etc" onClick={handleEditClick}>
              수정
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="실적보고서" />
            <ExcelButton data={allData} columns={columns} fileName="실적보고서" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`report-table-v${tableVer}`}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            maxHeight="100%"
            infiniteScroll={true}
            initialLoadCount={20}
            loadMoreCount={20}
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
          />
        </div>
      </main>
      <PerformanceModal isOpen={isModalOpen} onClose={handleModalClose} mode={modalMode} initialData={selectedData} />
    </div>
  );
}
